import type { CadenceStepTypePure, TemplateStepPure } from '../CGStep/types';

/**
 * Step shape a template comparison cares about. Matches the relevant subset
 * of `CadenceStepNodePure`.
 */
export type TemplateDiffStep = {
  type: CadenceStepTypePure;
  body2?: string | null;
  subject?: string | null;
  postsToLike?: number | null;
  onlyOpenLink?: boolean | null;
  cc?: string | null;
  bcc?: string | null;
  templateStepId?: string | null;
  templateStep?: TemplateStepPure | null;
};

/**
 * `null` and `1` are treated as the same default for `postsToLike` —
 * matches the existing CGLeafNodePure semantics so a freshly-instantiated
 * `likeRecentPosts` step doesn't appear "edited" just because its template
 * left the field unset.
 */
function postsToLikeNormalized(n: number | null | undefined): number {
  return n == null ? 1 : n;
}

function eq<T>(a: T | null | undefined, b: T | null | undefined): boolean {
  return (a ?? null) === (b ?? null);
}

/**
 * True when this step's content has been edited away from its template
 * default. Returns false when there is no template (a freshly added step has
 * `templateStepId == null` and is not "different from" anything).
 *
 * Compares only the fields each step type actually uses, mirroring the
 * legacy logic in CGLeafNodePure.isDifferentFromTemplate.
 */
export function isStepDifferentFromTemplate(step: TemplateDiffStep): boolean {
  if (step.templateStepId == null || step.templateStep == null) return false;
  const t = step.templateStep;
  if (t.type !== step.type) return true;

  switch (step.type) {
    case 'likeRecentPosts':
      return postsToLikeNormalized(step.postsToLike) !== postsToLikeNormalized(t.postsToLike);
    case 'visit':
    case 'follow':
      // Action-only types — no editable content beyond the type itself.
      return false;
    case 'connect':
      return !eq(t.body2, step.body2);
    case 'message':
      return !eq(t.body2, step.body2) || !eq(t.subject, step.subject);
    case 'reminder':
      return !eq(t.body2, step.body2);
    case 'inmail':
      return (
        !eq(t.body2, step.body2) ||
        !eq(t.subject, step.subject) ||
        !eq(t.onlyOpenLink, step.onlyOpenLink)
      );
    case 'email':
      return (
        !eq(t.body2, step.body2) ||
        !eq(t.subject, step.subject) ||
        !eq(t.cc, step.cc) ||
        !eq(t.bcc, step.bcc)
      );
    default:
      return false;
  }
}
